import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Avatar, 
  Tag, 
  Space, 
  Button, 
  Card, 
  Divider,
  Form, 
  Input, 
  List, 
  message,
  Skeleton,
  Modal
} from 'antd';
import { 
  CalendarOutlined, 
  UserOutlined, 
  EyeOutlined, 
  LikeOutlined,
  MessageOutlined,
  TagOutlined,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import PostCard from '../../components/PostCard';
import { posts, users } from '../../api/mockData';
import './index.css';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const PostDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);
  const [commentForm] = Form.useForm();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    setLoading(true);
    
    // 模拟 API 调用延迟
    setTimeout(() => {
      const foundPost = posts.find(p => p.id === id);
      
      if (foundPost) {
        setPost(foundPost);
        setLikes(foundPost.likes || 0);
        
        // 获取相关文章（相同标签或作者）
        const related = posts
          .filter(p => p.id !== id && (
            p.tags.some(tag => foundPost.tags.includes(tag)) ||
            p.author === foundPost.author
          ))
          .slice(0, 3);
        setRelatedPosts(related);
        
        // 获取评论数据（模拟）
        const postComments = [
          {
            id: 1,
            user: '张三',
            avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
            content: '这篇文章写得太好了，解决了我一直以来的困惑！',
            time: '2025-01-10 10:30:00',
            likes: 5
          },
          {
            id: 2,
            user: '李四',
            avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
            content: '感谢分享，期待更多关于这个主题的文章！',
            time: '2025-01-09 15:20:00',
            likes: 2
          },
          {
            id: 3,
            user: '王五',
            avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
            content: '实践了一下，确实有效，谢谢作者！',
            time: '2025-01-08 09:15:00',
            likes: 3
          }
        ];
        setComments(postComments);
      }
      
      // 获取当前用户
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        setCurrentUser(JSON.parse(userStr));
      }
      
      setLoading(false);
    }, 500);
  }, [id]);

  const handleLike = () => {
    if (liked) {
      setLikes(likes - 1);
      message.success('已取消点赞');
    } else {
      setLikes(likes + 1);
      message.success('点赞成功！');
    }
    setLiked(!liked);
  };

  const handleCommentSubmit = (values) => {
    if (!currentUser) {
      message.warning('请先登录后评论');
      navigate('/auth');
      return;
    }
    
    const newComment = {
      id: comments.length + 1,
      user: currentUser.name,
      avatar: currentUser.avatar,
      content: values.comment,
      time: new Date().toISOString().replace('T', ' ').substring(0, 19),
      likes: 0
    };
    
    setComments([newComment, ...comments]);
    commentForm.resetFields();
    message.success('评论发表成功！');
  };

  const handleEdit = () => {
    if (!currentUser || currentUser.id !== post.authorId) {
      message.warning('只有作者可以编辑文章');
      return;
    }
    navigate(`/write?edit=${id}`);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这篇文章吗？此操作不可恢复。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => {
        message.success('文章已删除（演示效果，实际数据未删除）');
        navigate('/');
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCoverImage = (category) => {
    const images = {
      '技术': 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1200&h=400&fit=crop',
      '生活': 'https://images.unsplash.com/photo-1519677100203-5f3a2d3335c7?w=1200&h=400&fit=crop',
      '旅行': 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&h=400&fit=crop',
      '美食': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=1200&h=400&fit=crop'
    };
    return images[category] || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200&h=400&fit=crop';
  };

  if (loading) {
    return (
      <Layout className="post-detail-page">
        <Content className="detail-container">
          <Skeleton active />
        </Content>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout className="post-detail-page">
        <Content className="detail-container">
          <div className="error-message">
            <Title level={3}>文章不存在</Title>
            <Text type="secondary">抱歉，您访问的文章可能已被删除或不存在。</Text>
            <div style={{ marginTop: '24px' }}>
              <Button type="primary" onClick={() => navigate('/')}>
                返回首页
              </Button>
            </div>
          </div>
        </Content>
      </Layout>
    );
  }

  const authorInfo = users.find(u => u.id === post.authorId) || {};

  return (
    <Layout className="post-detail-page">
      <Content className="detail-container">
        {/* 文章头部 */}
        <div className="post-header">
          <Button 
            type="link" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ marginBottom: '16px' }}
          >
            返回
          </Button>
          
          <Title level={1} className="post-title">
            {post.title}
          </Title>
          
          <div className="post-meta">
            <Space className="meta-item">
              <Avatar 
                src={authorInfo.avatar} 
                icon={<UserOutlined />}
                className="author-avatar"
              />
              <Text strong>{post.author}</Text>
            </Space>
            
            <Space className="meta-item">
              <CalendarOutlined />
              <Text>{formatDate(post.date)}</Text>
            </Space>
            
            <Space className="meta-item">
              <EyeOutlined />
              <Text>{post.readCount} 阅读</Text>
            </Space>
            
            <Space className="meta-item">
              <TagOutlined />
              <div>
                {post.tags.map(tag => (
                  <Tag key={tag} color="blue" style={{ margin: '2px' }}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </Space>
            
            <Tag color="cyan">{post.category}</Tag>
          </div>
          
          <img 
            src={getCoverImage(post.category)} 
            alt={post.title}
            className="post-cover"
          />
        </div>

        {/* 编辑按钮（作者可见） */}
        {currentUser && currentUser.id === post.authorId && (
          <div className="edit-actions">
            <Space>
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={handleEdit}
              >
                编辑文章
              </Button>
              <Button 
                danger 
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                删除文章
              </Button>
            </Space>
          </div>
        )}

        {/* 文章内容 */}
        <Card className="post-content">
          <div 
            className="post-body"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          
          <Divider />
          
          <div className="post-actions">
            <Button 
              className={`action-button ${liked ? 'liked' : ''}`}
              icon={<LikeOutlined />}
              onClick={handleLike}
            >
              {liked ? '已点赞' : '点赞'} ({likes})
            </Button>
            
            <Button 
              className="action-button"
              icon={<MessageOutlined />}
              onClick={() => {
                document.querySelector('.comment-form textarea')?.focus();
              }}
            >
              评论 ({comments.length})
            </Button>
            
            <Button 
              className="action-button"
              icon={<ShareAltOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                message.success('链接已复制到剪贴板');
              }}
            >
              分享
            </Button>
          </div>
        </Card>

        {/* 评论区域 */}
        <div className="comments-section">
          <Card title={`评论 (${comments.length})`}>
            {/* 评论表单 */}
            <Form
              form={commentForm}
              onFinish={handleCommentSubmit}
              className="comment-form"
            >
              <Form.Item
                name="comment"
                rules={[
                  { required: true, message: '请输入评论内容' },
                  { min: 5, message: '评论至少5个字符' },
                  { max: 500, message: '评论最多500个字符' }
                ]}
              >
                <TextArea 
                  rows={4}
                  placeholder="写下您的评论..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  disabled={!currentUser}
                >
                  {currentUser ? '发表评论' : '请先登录'}
                </Button>
              </Form.Item>
            </Form>
            
            {/* 评论列表 */}
            <List
              className="comment-list"
              dataSource={comments}
              renderItem={comment => (
                <List.Item className="comment-item">
                  <div style={{ width: '100%' }}>
                    <div className="comment-header">
                      <div className="comment-author">
                        <Avatar src={comment.avatar} icon={<UserOutlined />} />
                        <div>
                          <Text strong>{comment.user}</Text>
                          <div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              {formatDate(comment.time)}
                            </Text>
                          </div>
                        </div>
                      </div>
                      <Space className="comment-actions">
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<LikeOutlined />}
                        >
                          {comment.likes}
                        </Button>
                        <Button type="text" size="small">回复</Button>
                      </Space>
                    </div>
                    
                    <Paragraph className="comment-content">
                      {comment.content}
                    </Paragraph>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </div>

        {/* 相关文章 */}
        {relatedPosts.length > 0 && (
          <div className="related-posts">
            <Divider>
              <Title level={3}>相关推荐</Title>
            </Divider>
            
            <div className="related-grid">
              {relatedPosts.map(relatedPost => (
                <PostCard key={relatedPost.id} post={relatedPost} />
              ))}
            </div>
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default PostDetailPage;